/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * Component to show notification message
 *
 * @author Evgenij Tyan
 * @author Michael Kokoulin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.12.0
 * @since 1.0.0
 */

import { Fragment, memo, useCallback } from 'react';

import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import { connect, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { icons } from '../../../assets';
import { ImgStyled } from '../../../components/common/Block/Img';
import { LayoutStyled } from '../../../components/common/Block/Layout';
import ArrowTooltip from '../../../components/common/Tooltip/Tooltip';
import { IRootReducer } from '../../../reducers/rootReducer';
import GTMOperations from '../../analytics/GoogleTagManager/GTMOperations';
import { EUrlType } from '../../app/appConstants';
import { makeSelectAppErrorCode } from '../../app/appSelectors';
import { makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import ICommonTranslation from '../../internationalization/types/common';
import { closeNotificationAction } from '../notificationActions';
import {
    makeSelectIsOpenNotification,
    makeSelectNotificationCurrentShowing,
    makeSelectNotificationIsEnabled,
} from '../notificationSelectors';
import {
    ENotificationLevelTypes,
    ENotificationPlace,
    ENotificationTypes,
    INotification,
} from '../types/notificationTypes';
import { NoticeIcon, NoticeMessageStyled, NoticeStyled } from './Styled/noticeStyled';

export interface INoticeMessageProps {
    place?: ENotificationPlace;
}

const getNoticeIcon = (notification: INotification) => {
    switch (notification.type) {
        case ENotificationTypes.AUTH:
            return <NoticeIcon>!</NoticeIcon>;
        case ENotificationTypes.OUTSTANDING_DEBT:
        case ENotificationTypes.UNPAID_BILLS:
            return (
                <LayoutStyled className="mr-3" width="20">
                    <ImgStyled className="small" src={icons.balanceWhiteIcon}/>
                </LayoutStyled>
            );
    }
};

const getMessageText = (
    props: Pick<TNoticeMessage, 'translations' | 'errorCode'> & { notification: INotification | undefined },
) => {
    const { notification, translations, errorCode } = props;
    if (notification && notification.message) {
        return notification.message;
    }
    if (errorCode) {
        return translations.error.codes[errorCode] || `${translations.error.defaultMessage} (${errorCode})`;
    }
    return translations.error.defaultMessage;
};

export const getNotificationLinkType = (notificationType?: ENotificationTypes): EUrlType => {
    if (!notificationType) return EUrlType.INTERNAL;

    switch (notificationType) {
        case ENotificationTypes.AUTH:
            return EUrlType.EXTERNAL;
        case ENotificationTypes.UNPAID_BILLS:
            return EUrlType.INTERNAL;
        case ENotificationTypes.AUTH_SERVICE_ACCESS:
        case ENotificationTypes.OUTSTANDING_DEBT:
        default:
            return EUrlType.INTERNAL;
    }
};

export type TNoticeMessage = INoticeMessageProps & IDispatchProps & IConnectedProps;

export const NoticeMessage = (props: TNoticeMessage) => {
    const {
        closeNotification,
        place,
        translations,
        errorCode,
    } = props;

    const isOpen = useSelector(makeSelectIsOpenNotification(place || ENotificationPlace.global));
    const notification = useSelector(makeSelectNotificationCurrentShowing(place || ENotificationPlace.global));
    const isEnabled = useSelector(makeSelectNotificationIsEnabled(place || ENotificationPlace.global));

    const handleNotificationLinkClick = (message: string, notification: INotification | undefined) => {
        GTMOperations.navigation.onNotificationLinkClick(message);
        if (notification?.type === ENotificationTypes.AUTH_SERVICE_ACCESS) {
            closeNotification();
        }
    };

    const getNotification = useCallback((): INotification[] | undefined =>
            isArray(notification)
                ? notification
                : isObject(notification) ? [notification] : undefined
        , [notification]);

    return (
        <Fragment>
            {
                !!getNotification() && isOpen && isEnabled && (
                    getNotification()?.map(notification => (
                        <NoticeStyled
                            key={notification.message}
                            kind={ENotificationLevelTypes[notification.level].toLowerCase()}
                        >
                            <NoticeMessageStyled>
                                {getNoticeIcon(notification)}
                                {getMessageText({ translations, errorCode, notification })}
                                {
                                    notification.urls?.map(notifier => {
                                        const url = notifier.url;
                                        const preUrl = url.slice(0, 1) === '/' || url.startsWith('http') ? '' : '/';
                                        return url && (
                                            <Fragment key={url}>
                                                <ArrowTooltip title={notifier.tooltip} titleAlign="center">
                                                <span className="notice__link" key={url}>
                                                    &nbsp;
                                                    {
                                                        getNotificationLinkType(notification?.type) ===
                                                          EUrlType.EXTERNAL
                                                            ? <a
                                                                href={preUrl + url}
                                                                onClick={() => handleNotificationLinkClick(
                                                                    notifier.label ?? '', notification)}
                                                            >
                                                                {notifier.label}
                                                            </a>
                                                            : <Link
                                                                to={preUrl + url}
                                                                onClick={() => handleNotificationLinkClick(
                                                                    notifier.label ?? '', notification)}
                                                            >
                                                                {notifier.label}
                                                            </Link>
                                                    }
                                                </span>
                                                </ArrowTooltip>
                                            </Fragment>
                                        );
                                    })
                                }
                            </NoticeMessageStyled>
                        </NoticeStyled>
                    ))
                )}
        </Fragment>
    );
};

export interface IConnectedProps {
    errorCode?: string;
    translations: ICommonTranslation;
}

const mapStateToProps = () => createStructuredSelector<IRootReducer, IConnectedProps>({
    errorCode: makeSelectAppErrorCode(),
    translations: makeSelectTranslationCommon(),
});

interface IDispatchProps {
    closeNotification: () => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    closeNotification: () => dispatch(closeNotificationAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(NoticeMessage));
